import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { Select } from "../../../../ui/components/Select";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import {
  FeatherArrowLeft,
  FeatherUser,
  FeatherMail,
  FeatherLock,
} from "@subframe/core";
import { useUserManagement } from "../hooks/useUserManagement";
import { useToast } from "../../../../shared/presentation/components";
import { getToken } from "../../../../shared/data/storage/authStorage";
import { PermissionCards } from "../components/PermissionCards";
import { PermissionRepositoryImpl } from "../../data/repositories/PermissionRepositoryImpl";
import { API_BASE_URL } from "../../../../config/api";

const permissionRepository = new PermissionRepositoryImpl();

interface Role {
  id: number;
  role_name: string;
  is_manager_required: boolean | number | string; // Can be boolean, number (0/1), or string ("0"/"1")
  description?: string;
}

// Helper function to check if manager is required - SIMPLE AND DIRECT
const isManagerRequiredForRole = (role: Role | undefined): boolean => {
  if (!role) {
    return false;
  }

  const value = role.is_manager_required;

  // If null/undefined, return false
  if (value === null || value === undefined) {
    return false;
  }

  // Convert to string, trim, and check if it's "1" or "true"
  const strValue = String(value).trim().toLowerCase();
  if (strValue === "1" || strValue === "true") {
    return true;
  }

  // Check if it's the number 1
  if (typeof value === "number" && value === 1) {
    return true;
  }

  // Check if it's boolean true
  if (typeof value === "boolean" && value === true) {
    return true;
  }

  return false;
};

export const AddUserPage: React.FC = () => {
  const navigate = useNavigate();
  const { createUser, fetchAssignableManagers, assignableManagers } =
    useUserManagement();
  const { showSuccess, showError } = useToast();

  const [formData, setFormData] = useState({
    userName: "",
    email: "",
    password: "",
    role_id: undefined as number | undefined,
    manager_id: undefined as number | undefined,
  });

  const [roles, setRoles] = useState<Role[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [rolePermissions, setRolePermissions] = useState<Record<string, any>>(
    {}
  );
  const [userPermissions, setUserPermissions] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  // Fetch roles and assignable managers on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        const token = getToken();
        if (!token) {
          showError("Authentication required");
          return;
        }

        // Fetch roles from new endpoint
        const rolesResponse = await fetch(`${API_BASE_URL}/api/roles/list`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (rolesResponse.ok) {
          const rolesData = await rolesResponse.json();
          if (rolesData.success) {
            setRoles(rolesData.data);
          }
        }

        await fetchAssignableManagers();
      } catch (error) {
        console.error("Error loading data:", error);
        showError("Failed to load roles and managers");
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, [fetchAssignableManagers, showError]);

  // Fetch role permissions when role changes
  useEffect(() => {
    const loadRolePermissions = async () => {
      if (!formData.role_id) {
        setRolePermissions({});
        setUserPermissions({});
        return;
      }

      try {
        setLoadingPermissions(true);
        // Fetch role permissions using role_id
        const rolePerms = await permissionRepository.getRolePermissions(
          formData.role_id
        );

        // Convert to the format PermissionCards expects
        const rolePermsFormatted: Record<string, any> = {};
        const userPermsFormatted: Record<string, Record<string, boolean>> = {};

        Object.entries(rolePerms).forEach(
          ([moduleCode, moduleData]: [string, any]) => {
            rolePermsFormatted[moduleCode] = {
              module_name: moduleData.module_name,
              actions: {},
            };

            if (!userPermsFormatted[moduleCode]) {
              userPermsFormatted[moduleCode] = {};
            }

            Object.entries(moduleData.actions).forEach(
              ([actionCode, actionData]: [string, any]) => {
                rolePermsFormatted[moduleCode].actions[actionCode] = {
                  action_name: actionData.action_name,
                  is_granted: actionData.is_granted,
                  requires_review: actionData.requires_review,
                };

                // Set effective permissions (from role)
                userPermsFormatted[moduleCode][actionCode] =
                  actionData.is_granted;
              }
            );
          }
        );

        setRolePermissions(rolePermsFormatted);
        setUserPermissions(userPermsFormatted);
      } catch (error) {
        console.error("Error loading role permissions:", error);
        showError("Failed to load role permissions");
        setRolePermissions({});
        setUserPermissions({});
      } finally {
        setLoadingPermissions(false);
      }
    };

    if (formData.role_id) {
      loadRolePermissions();
    }
  }, [formData.role_id, showError]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleRoleChange = (value: string) => {
    const roleId = value ? parseInt(value, 10) : undefined;
    // Use type-safe comparison to find role
    const selectedRole = roles.find((r) => {
      if (roleId === undefined) return false;
      return (
        r.id === roleId ||
        Number(r.id) === Number(roleId) ||
        String(r.id) === String(roleId)
      );
    });
    const roleRequiresManager = isManagerRequiredForRole(selectedRole);

    setFormData((prev) => ({
      ...prev,
      role_id: roleId,
      // Only reset manager if the new role doesn't require it
      manager_id: roleRequiresManager ? prev.manager_id : undefined,
    }));

    // Clear manager error when role changes
    if (errors.role_id) {
      setErrors((prev) => ({ ...prev, role_id: "" }));
    }
    if (errors.manager_id) {
      setErrors((prev) => ({ ...prev, manager_id: "" }));
    }
  };

  const handleManagerChange = (value: string) => {
    const managerId =
      value === "none" || value === "" ? undefined : parseInt(value, 10);
    setFormData((prev) => ({ ...prev, manager_id: managerId }));
    if (errors.manager_id) {
      setErrors((prev) => ({ ...prev, manager_id: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.userName.trim()) {
      newErrors.userName = "Username is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    // Password is optional - user will change it via email later
    if (formData.password.trim() && formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.role_id) {
      newErrors.role_id = "Role is required";
    }

    // Check if selected role requires manager
    if (formData.role_id) {
      const selectedRole = roles.find((r) => {
        return (
          r.id === formData.role_id ||
          Number(r.id) === Number(formData.role_id) ||
          String(r.id) === String(formData.role_id)
        );
      });
      const roleRequiresManager = isManagerRequiredForRole(selectedRole);
      if (roleRequiresManager && !formData.manager_id) {
        newErrors.manager_id = "Manager is required for this role";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Double-check manager requirement before submitting
    if (formData.role_id) {
      const selectedRole = roles.find((r) => {
        return (
          r.id === formData.role_id ||
          Number(r.id) === Number(formData.role_id) ||
          String(r.id) === String(formData.role_id)
        );
      });
      const roleRequiresManager = isManagerRequiredForRole(selectedRole);
      if (roleRequiresManager && !formData.manager_id) {
        setErrors((prev) => ({
          ...prev,
          manager_id: "Manager is required for this role",
        }));
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);

    try {
      await createUser({
        email: formData.email,
        userName: formData.userName,
        password: formData.password.trim(),
        role_id: formData.role_id!,
        manager_id: formData.manager_id,
      });

      showSuccess("User created successfully");
      navigate("/admin/dashboard");
    } catch (error: any) {
      showError(error.message || "Failed to create user");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/dashboard");
  };

  // Calculate if manager is required - recalculate whenever role_id or roles change
  const selectedRole = roles.find((r) => {
    // Try both number and string comparison
    return (
      r.id === formData.role_id ||
      Number(r.id) === Number(formData.role_id) ||
      String(r.id) === String(formData.role_id)
    );
  });
  const isManagerRequired = isManagerRequiredForRole(selectedRole);

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6 p-8">
        {/* Header */}
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={<FeatherArrowLeft />}
              onClick={handleCancel}
              disabled={isLoading}
            >
              Back
            </Button>
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Create New User
              </span>
              <span className="text-body font-body text-subtext-color">
                Create a new user account with role and manager assignment
              </span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex w-full flex-col items-start gap-6"
        >
          {/* Basic Information Section */}
          <div className="flex w-full flex-col gap-6 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Basic Information
            </span>

            <TextField
              className="h-auto w-full flex-none"
              label="Username *"
              helpText={errors.userName || ""}
              error={!!errors.userName}
              icon={<FeatherUser />}
            >
              <TextField.Input
                name="userName"
                type="text"
                placeholder="Enter username"
                value={formData.userName}
                onChange={handleChange}
                disabled={isLoading || loadingData}
              />
            </TextField>

            <TextField
              className="h-auto w-full flex-none"
              label="Email Address *"
              helpText={errors.email || ""}
              error={!!errors.email}
              icon={<FeatherMail />}
            >
              <TextField.Input
                name="email"
                type="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading || loadingData}
              />
            </TextField>

            <TextField
              className="h-auto w-full flex-none"
              label="Password *"
              helpText={errors.password || ""}
              error={!!errors.password}
              icon={<FeatherLock />}
            >
              <TextField.Input
                name="password"
                placeholder="Enter password"
                value={formData.password}
                onChange={handleChange}
                disabled={isLoading || loadingData}
              />
            </TextField>

            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body font-body text-default-font">
                Role *
              </span>
              <Select
                label=""
                placeholder="Select role"
                className="h-auto w-full flex-none"
                value={formData.role_id?.toString() || ""}
                onValueChange={handleRoleChange}
                disabled={isLoading || loadingData || loadingPermissions}
              >
                {roles.map((role) => (
                  <Select.Item key={role.id} value={role.id.toString()}>
                    {role.role_name}
                  </Select.Item>
                ))}
              </Select>
              {errors.role_id && (
                <span className="text-caption font-caption text-error-700">
                  {errors.role_id}
                </span>
              )}
            </div>

            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body font-body text-default-font">
                {isManagerRequired ? "Manager *" : "Manager"}
              </span>
              {isManagerRequired ? (
                <Select
                  key={`manager-required-${formData.role_id}`}
                  label=""
                  placeholder="Select manager *"
                  className="h-auto w-full flex-none"
                  value={formData.manager_id?.toString() || ""}
                  onValueChange={handleManagerChange}
                  disabled={isLoading || loadingData || loadingPermissions}
                >
                  {assignableManagers.length === 0 ? (
                    <Select.Item value="" disabled>
                      No managers available
                    </Select.Item>
                  ) : (
                    assignableManagers.map((manager) => (
                      <Select.Item
                        key={manager.id}
                        value={manager.id.toString()}
                      >
                        {manager.userName || manager.email} ({manager.role})
                      </Select.Item>
                    ))
                  )}
                </Select>
              ) : (
                <Select
                  key={`manager-optional-${formData.role_id}`}
                  label=""
                  placeholder="Select manager (optional)"
                  className="h-auto w-full flex-none"
                  value={formData.manager_id?.toString() || "none"}
                  onValueChange={handleManagerChange}
                  disabled={isLoading || loadingData || loadingPermissions}
                >
                  <Select.Item value="none">No Manager</Select.Item>
                  {assignableManagers.map((manager) => (
                    <Select.Item key={manager.id} value={manager.id.toString()}>
                      {manager.userName || manager.email} ({manager.role})
                    </Select.Item>
                  ))}
                </Select>
              )}
              {isManagerRequired &&
                !formData.manager_id &&
                !errors.manager_id && (
                  <span className="text-caption font-caption text-subtext-color">
                    Manager is required for this role
                  </span>
                )}
              {errors.manager_id && (
                <span className="text-caption font-caption text-error-700">
                  {errors.manager_id}
                </span>
              )}
            </div>
          </div>

          {/* Permissions Section - Read Only */}
          {loadingPermissions ? (
            <div className="flex w-full items-center justify-center py-8">
              <span className="text-body font-body text-subtext-color">
                Loading permissions...
              </span>
            </div>
          ) : formData.role_id &&
            rolePermissions &&
            Object.keys(rolePermissions).length > 0 ? (
            <div className="flex w-full flex-col gap-6 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex flex-col gap-2">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Role Permissions
                </span>
                <p className="text-body font-body text-subtext-color">
                  Permissions for the selected role. These permissions will be
                  assigned to the user.
                </p>
              </div>
              <PermissionCards
                rolePermissions={rolePermissions}
                userPermissions={userPermissions}
                onChange={() => {}} // No-op since read-only
                readOnly={true}
              />
            </div>
          ) : null}

          {/* Actions */}
          <div className="flex w-full items-end justify-end gap-3 border-t border-solid border-neutral-border pt-6">
            <Button
              type="button"
              variant="neutral-secondary"
              onClick={handleCancel}
              disabled={isLoading || loadingData || loadingPermissions}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              disabled={
                isLoading ||
                loadingData ||
                loadingPermissions ||
                !formData.role_id
              }
              loading={isLoading}
            >
              {isLoading ? "Creating..." : "Create User"}
            </Button>
          </div>
        </form>
      </div>
    </DefaultPageLayout>
  );
};
